import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  acharModelo, LEMBRETES_PADRAO, resolverLembretes, type LembreteId, type Lembretes,
} from "@nhomi/core";
import { useSession } from "@/lib/auth";
import { ligar, permissao, suportado, temChave } from "@/lib/push";
import { lerPerfil, salvarLembretes } from "@/data/users";

/* ============================================================
   Pedir aviso na hora certa

   Antes, a permissão de notificação só era pedida em Conta → habilitar.
   Quem instalava e nunca entrava no perfil jamais recebia aviso nenhum e
   não tinha como saber que existia — o app parecia mudo.

   O CONSERTO NÃO É PEDIR NA ABERTURA. Disparar a caixa do sistema assim
   que o app abre é o jeito mais rápido de ouvir "não": a pessoa ainda não
   sabe o que o app faz, e a recusa do Android é PERMANENTE — não dá pra
   perguntar de novo, nem pelo perfil. Um "não" apressado desliga o aviso
   pra sempre naquele aparelho.

   Então isto pergunta ANTES, do nosso jeito, e só chama a caixa do
   sistema depois do sim. E só aparece pra quem já está num desafio: sem
   grupo não há o que avisar, e o pedido soaria gratuito.

   O "agora não" é gravado no aparelho — insistir a cada abertura é como
   se perde a boa vontade que sobrou.

   ---------- e o lembrete de refeição vem junto ----------

   Este card listava prato, comentário e bate-papo e terminava em "Nada
   além disso". Era verdade e era o problema: o único aviso que traz
   alguém na HORA DE COMER — que é a hora em que este app tem alguma
   coisa a pedir — não estava na lista, nascia desligado e morava em
   Conta, uma tela que a maioria nunca abre. O aviso de maior valor
   estava escondido atrás do menu, e a pergunta era feita sem mencioná-lo.

   Agora são dois passos, e o segundo só existe depois do PRIMEIRO SIM:
   pedir a permissão do sistema e escolher horário na mesma tela seria
   pedir duas decisões antes de qualquer uma valer.

   O segundo passo liga DOIS slots, não os quatro. Ligar quatro avisos
   diários em quem tocou num botão só é o caminho mais curto pra pessoa
   desligar tudo no sistema — e aí vão junto o comentário e a nota, que
   ela queria receber. Almoço e janta são as duas refeições que quase
   todo mundo faz em horário parecido; café e lanche ficam a um toque em
   Conta, que o próprio card aponta.
   ============================================================ */

const LEMBRETE = "nhomi.avisos.dispensado";

/** Os dois que o botão liga. A escolha está explicada acima. */
const SUGERIDOS: LembreteId[] = ["almoco", "janta"];

type Passo = "permissao" | "lembretes";

export function PedirAvisos({ temDesafio }: { temDesafio: boolean }) {
  const { user } = useSession();
  const [mostrar, setMostrar] = useState(false);
  const [passo, setPasso] = useState<Passo>("permissao");
  const [ocupado, setOcupado] = useState(false);

  useEffect(() => {
    let vivo = true;
    void (async () => {
      if (!user || !temDesafio || !temChave()) return;
      // "default" é quem nunca respondeu. Quem já disse não fica de fora:
      // a caixa não abriria de novo, e o convite viraria uma parede.
      if (permissao() !== "default") return;
      if (!await suportado()) return;
      try {
        if (localStorage.getItem(LEMBRETE)) return;
      } catch {
        // Navegador com armazenamento bloqueado: mostrar uma vez por
        // sessão é melhor que nunca mostrar.
      }
      if (vivo) setMostrar(true);
    })();
    return () => { vivo = false; };
  }, [user, temDesafio]);

  if (!mostrar || !user) return null;
  const uid = user.uid;

  function dispensar() {
    try { localStorage.setItem(LEMBRETE, "1"); } catch { /* sem espaço, tudo bem */ }
    setMostrar(false);
  }

  /**
   * Liga os dois slots sugeridos SEM apagar o que já estava lá.
   *
   * O perfil é relido antes de gravar porque `salvarLembretes` escreve o
   * objeto inteiro: partir do padrão desligaria um lembrete que a pessoa
   * tivesse ligado em Conta antes de chegar aqui. É improvável e é
   * exatamente o tipo de perda que ninguém relaciona com este botão.
   */
  async function ligarOsLembretes() {
    const perfil = await lerPerfil(uid);
    const atuais = perfil ? resolverLembretes(perfil.lembretes) : LEMBRETES_PADRAO;
    const novos: Lembretes = {
      ...atuais,
      slots: atuais.slots.map((s) =>
        (SUGERIDOS.includes(s.id) ? { ...s, ligado: true } : s)),
    };
    await salvarLembretes(uid, novos);
  }

  if (passo === "lembretes") {
    const horas = SUGERIDOS
      .map((id) => acharModelo(id))
      .filter((m): m is NonNullable<typeof m> => !!m);

    return (
      <div className="pad">
        <div className="card aviso-pedido">
          <div className="aviso-pedido-t">E na hora de comer, a gente lembra?</div>
          <p className="aviso-pedido-p">
            Um toque no celular na hora da refeição, com o que o grupo já
            postou. Só sai se você ainda não registrou aquela refeição — e
            some sozinho quando o desafio acaba.
          </p>
          <div className="aviso-pedido-b">
            <button className="btn btn-primary" disabled={ocupado}
              onClick={async () => {
                setOcupado(true);
                try { await ligarOsLembretes(); } catch { /* Conta mostra o porquê */ }
                setOcupado(false);
                setMostrar(false);
              }}>
              {ocupado
                ? "Um instante…"
                : `Me lembrar ${horas.map((m) => `${m.emoji} ${m.hora}`).join(" e ")}`}
            </button>
            <button className="btn btn-ghost" onClick={() => setMostrar(false)}>
              Agora não
            </button>
          </div>
          <div className="hint-row">
            Isso liga almoço e janta. Café, lanche e os horários ficam
            em <Link to="/conta">Conta</Link>.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pad">
      <div className="card aviso-pedido">
        <div className="aviso-pedido-t">Quer saber quando a galera postar?</div>
        <p className="aviso-pedido-p">
          A gente avisa quando alguém publica um prato, comenta no seu ou
          manda recado no bate-papo — e pode te lembrar na hora das
          refeições, se você quiser.
        </p>
        <div className="aviso-pedido-b">
          <button className="btn btn-primary" disabled={ocupado}
            onClick={async () => {
              setOcupado(true);
              /* O resultado decide o PRÓXIMO passo, e só isso. Quem disse
                 não na caixa do sistema não pode receber lembrete nenhum,
                 então oferecer horário ali seria oferecer uma tela que não
                 ia funcionar. */
              let ligou = false;
              try { ligou = (await ligar(uid)).ok; } catch { /* o perfil mostra o porquê */ }
              try { localStorage.setItem(LEMBRETE, "1"); } catch { /* tudo bem */ }
              setOcupado(false);
              if (ligou) setPasso("lembretes"); else setMostrar(false);
            }}>
            {ocupado ? "Um instante…" : "Pode avisar"}
          </button>
          <button className="btn btn-ghost" onClick={dispensar}>Agora não</button>
        </div>
      </div>
    </div>
  );
}
